import { ColorRank } from "../types/battle"
import {
  VirtualTournamentCurrent,
  VirtualTournamentDetail,
  VirtualTournamentEntry,
  VirtualTournamentHistoryItem,
  VirtualTournamentMatch,
  VirtualTournamentParticipant,
  VirtualTournamentRankSummary,
  VirtualTournamentStatus,
} from "../types/virtualTournament"
import { normalizeColorRank } from "./radarStatus"
import { normalizeGameCard } from "./gameCards"

const TOURNAMENT_STATUSES: VirtualTournamentStatus[] = [
  "REGISTRATION",
  "BRACKET_READY",
  "IN_PROGRESS",
  "FINISHED",
  "CANCELLED",
]

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1" || value === "true") return true
  if (value === 0 || value === "0" || value === "false") return false
  return fallback
}

function pickStatus(value: unknown): VirtualTournamentStatus {
  const raw = pickString(value).toUpperCase()
  return TOURNAMENT_STATUSES.includes(raw as VirtualTournamentStatus)
    ? (raw as VirtualTournamentStatus)
    : "REGISTRATION"
}

function normalizePassport(raw: unknown): VirtualTournamentParticipant["passport"] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const nickname = pickString(obj.nickname)
  const publicGameCode = pickString(
    obj.publicGameCode ?? obj.public_game_code ?? obj.code
  )
  if (!nickname && !publicGameCode) return null
  return { nickname, publicGameCode }
}

function normalizeEntry(raw: unknown): VirtualTournamentEntry | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const participantId = pickString(
    obj.participantId ?? obj.participant_id ?? obj.id
  )
  const cardRaw = obj.card ?? obj.gameCard ?? obj.game_card
  const cardId = pickString(
    obj.cardId ??
      obj.card_id ??
      (cardRaw && typeof cardRaw === "object" && !Array.isArray(cardRaw)
        ? (cardRaw as Record<string, unknown>).id
        : undefined)
  )
  const card = normalizeGameCard(cardRaw)
  if (!participantId || !cardId) return null
  return { participantId, cardId, card: card ?? null }
}

export function normalizeVirtualTournamentRankSummary(
  raw: unknown
): VirtualTournamentRankSummary | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)
  const tournamentId = pickString(obj.tournamentId ?? obj.tournament_id ?? obj.id)
  if (!colorRank || !tournamentId) return null
  return {
    colorRank,
    tournamentId,
    status: pickStatus(obj.status),
    participantCount: pickNumber(
      obj.participantCount ??
        obj.participant_count ??
        (obj._count && typeof obj._count === "object"
          ? (obj._count as Record<string, unknown>).participants
          : undefined)
    ),
    minParticipants: pickNumber(obj.minParticipants ?? obj.min_participants, 8),
    maxParticipants: pickNumber(obj.maxParticipants ?? obj.max_participants, 32),
    myEntry: normalizeEntry(obj.myEntry ?? obj.my_entry),
  }
}

export function normalizeVirtualTournamentCurrent(
  raw: unknown
): VirtualTournamentCurrent | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const ranksRaw = Array.isArray(obj.ranks) ? obj.ranks : []
  const ranks = ranksRaw
    .map(normalizeVirtualTournamentRankSummary)
    .filter((item): item is VirtualTournamentRankSummary => item != null)

  return {
    weekStart: pickString(obj.weekStart ?? obj.week_start),
    registrationOpen: pickBool(obj.registrationOpen ?? obj.registration_open),
    nextStartAt: pickString(obj.nextStartAt ?? obj.next_start_at),
    msUntilStart: pickNumber(obj.msUntilStart ?? obj.ms_until_start),
    currentWeekStartAt: pickString(
      obj.currentWeekStartAt ?? obj.current_week_start_at
    ),
    ranks,
  }
}

export function normalizeVirtualTournamentParticipant(
  raw: unknown
): VirtualTournamentParticipant | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const id = pickString(obj.id ?? obj.participantId ?? obj.participant_id)
  if (!id) return null
  const statusRaw = pickString(obj.status).toUpperCase()
  const status =
    statusRaw === "WINNER" ||
    statusRaw === "ELIMINATED" ||
    statusRaw === "ACTIVE" ||
    statusRaw === "REGISTERED"
      ? statusRaw
      : "REGISTERED"
  return {
    id,
    status,
    passport: normalizePassport(obj.passport ?? obj.user ?? obj.player),
    card: normalizeGameCard(obj.card),
    seed: pickNumber(obj.seed, 0) || null,
  }
}

export function normalizeVirtualTournamentMatch(
  raw: unknown
): VirtualTournamentMatch | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const id = pickString(obj.id ?? obj.matchId ?? obj.match_id)
  if (!id) return null
  const statusRaw = pickString(obj.status).toUpperCase()
  const status =
    statusRaw === "IN_PROGRESS" ||
    statusRaw === "FINISHED" ||
    statusRaw === "BYE"
      ? statusRaw
      : "PENDING"
  const participant1 = normalizeVirtualTournamentParticipant(
    obj.participant1 ?? obj.participant_1 ?? obj.player1 ?? obj.player_1
  )
  const participant2 = normalizeVirtualTournamentParticipant(
    obj.participant2 ?? obj.participant_2 ?? obj.player2 ?? obj.player_2
  )
  return {
    id,
    round: pickNumber(obj.round ?? obj.roundNumber ?? obj.round_number, 1),
    status,
    participant1Id: pickString(
      obj.participant1Id ??
        obj.participant1_id ??
        obj.player1Id ??
        obj.player1_id ??
        participant1?.id
    ) || null,
    participant2Id: pickString(
      obj.participant2Id ??
        obj.participant2_id ??
        obj.player2Id ??
        obj.player2_id ??
        participant2?.id
    ) || null,
    winnerId:
      pickString(obj.winnerId ?? obj.winner_id ?? obj.winnerParticipantId) ||
      null,
    participant1,
    participant2,
    autoSimulated: pickBool(obj.autoSimulated ?? obj.auto_simulated),
  }
}

export function normalizeVirtualTournamentDetail(
  raw: unknown
): VirtualTournamentDetail | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const id = pickString(obj.id ?? obj.tournamentId ?? obj.tournament_id)
  if (!id) return null
  const colorRank = normalizeColorRank(
    obj.virtualColorRank ?? obj.virtual_color_rank ?? obj.colorRank ?? obj.color_rank
  )
  if (!colorRank) return null

  const participantsRaw = Array.isArray(obj.participants) ? obj.participants : []
  const matchesRaw = Array.isArray(obj.matches) ? obj.matches : []

  return {
    id,
    title: pickString(obj.title, `Виртуальный турнир — ${colorRank}`),
    virtualColorRank: colorRank,
    sourceWeekStart: pickString(obj.sourceWeekStart ?? obj.source_week_start),
    status: pickStatus(obj.status),
    participants: participantsRaw
      .map(normalizeVirtualTournamentParticipant)
      .filter((item): item is VirtualTournamentParticipant => item != null),
    matches: matchesRaw
      .map(normalizeVirtualTournamentMatch)
      .filter((item): item is VirtualTournamentMatch => item != null),
    liveMatchId:
      pickString(
        obj.liveMatchId ??
          obj.live_match_id ??
          (obj.liveMatch as { id?: string } | undefined)?.id
      ) ||
      null,
  }
}

export function normalizeVirtualTournamentHistoryItem(
  raw: unknown
): VirtualTournamentHistoryItem | null {
  const detail = normalizeVirtualTournamentDetail(raw)
  if (!detail) return null
  const obj = raw as Record<string, unknown>
  const countObj =
    obj._count && typeof obj._count === "object"
      ? (obj._count as Record<string, unknown>)
      : null
  const participantCount = pickNumber(
    countObj?.participants ??
      obj.participantCount ??
      obj.participant_count ??
      detail.participants.length
  )
  return {
    id: detail.id,
    title: detail.title,
    virtualColorRank: detail.virtualColorRank,
    sourceWeekStart: detail.sourceWeekStart,
    status: detail.status,
    participants: detail.participants,
    participantCount,
  }
}

export function findMyRegisteredRank(
  ranks: VirtualTournamentRankSummary[]
): VirtualTournamentRankSummary | null {
  return ranks.find((rank) => rank.myEntry != null) ?? null
}

export function formatVirtualTournamentRankLabel(rank: ColorRank): string {
  const labels: Record<ColorRank, string> = {
    WHITE: "Белый",
    GREEN: "Зелёный",
    BLUE: "Синий",
    PURPLE: "Фиолетовый",
    ORANGE: "Оранжевый",
    RED: "Красный",
  }
  return labels[rank]
}
